import React, { useRef, useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components/macro';

import { getParents, getUrl } from '../../utils/index';
import constantes from '../../resources/constantes.json';
import { snap } from '../../resources/constantes.json';
import InlineDialog, { useStateWithFocusManagement } from '../InlineDialog';
import { useTranslate } from '../../contexts/languageContext';
import DotsIcon from '../svg/Dots';
import CloseIcon from '../svg/close';
import List from './components/List';
import Dropdown from './components/Dropdown';
import Link from './components/Link';
import Tooltip from '../Tooltip/index.js';
import ArrowIcon from '../svg/Arrow';
import Dots from '../svg/Dots';
import { useParentLevel, useMesa } from '../../contexts/pageContext';
/**
 * Menu de navegación que permite navegar por los distintos ámbitos de forma jerárquica mediante una
 * serie de dropdowns y una lista.
 */

const NavMenu = ({
  nomenclator,
  eleccion,
  page,
  ambito,
  className,
  parentLevel,
  children,
}) => {
  const t = useTranslate();
  const openButtonRef = useRef();
  const [isOpen, setIsOpen] = useStateWithFocusManagement(false, openButtonRef);

  return (
    <div className={className}>
      <OpenButton
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        ref={openButtonRef}
        aria-label={
          isOpen
            ? t('cerrarMenuNavegacionPorAmbitos')
            : t('abrirMenuNavegacionPorAmbitos')
        }
      >
        <Flex>
          <Text>{children}</Text>
          <ArrowIcon
            css="padding-left: 10px; align-self: center;"
            variant={isOpen ? 'up' : 'down'}
            fill="#fff"
          />
        </Flex>
      </OpenButton>
      {isOpen && (
        <StyledInlineDialog
          onClose={e => {
            //setTimeout para que si le damos al OpenButton non se cierrre y se abra instantaneamente
            //TODO: comprobar que este truco funciona en todos los navegadores (Firefox y Chrome Ok).
            setTimeout(() => setIsOpen(false), 0);
          }}
        >
          <StyledNavMenuContent
            nomenclator={nomenclator}
            eleccion={eleccion}
            ambito={ambito}
            page={page}
            setIsOpen={setIsOpen}
            parentLevel={parentLevel}
          />
        </StyledInlineDialog>
      )}
      {snap && (
        <StyledNavMenuContent
          nomenclator={nomenclator}
          eleccion={eleccion}
          ambito={ambito}
          page={page}
          setIsOpen={setIsOpen}
          parentLevel={parentLevel}
          css="display: none;"
        />
      )}
    </div>
  );
};

const Flex = styled.span`
  box-sizing: border-box;
  display: flex;
  align-items: baseline;
  line-height: 0;
  cursor: pointer;
  margin-left: auto;
`;

const Text = styled.span`
  font-size: 14px;
  font-weight: bold;
  line-height: 1.3;
  color: #fff;
  @media print {
    color: #1c4183;
    }
`;

const NavMenuContent = withRouter(function NavMenuContent({
  nomenclator,
  history,
  eleccion,
  ambito,
  page,
  className = '',
  setIsOpen,
  parentLevel,
}) {
  const t = useTranslate();
  const levelText = {
    [constantes.level.departamento]: t('departamentosDC'),
    [constantes.level.municipio]: t('municipios'),
    [constantes.level.zona]: t('zonas'),
    [constantes.level.comuna]: t('comunas'),
    [constantes.level.puesto]: t('puestos'),
    [constantes.level.mesa]: t('mesas'),
  };
  const mesa = useMesa();
  const fullGenealogy = [
    ...getParents(ambito.i, eleccion, nomenclator, parentLevel).reverse(),
    ambito.i,
  ];
  const dropdownGenealogy = fullGenealogy.slice(1);
  let dropdownIds = [...dropdownGenealogy];
  if (
    ambito.l !== constantes.level.puesto &&
    (!ambito.h || ambito.h.length === 0)
  ) {
    dropdownIds = dropdownIds.slice(0, -1);
  }
  const root = fullGenealogy[0];
  const ambitos = nomenclator.ambitos[eleccion];
  const childs = ambitos[root].h.map(id => ambitos[id]);

  return (
    <div className={className}>
      <LeftPane>
        <Title>
          <Link
            to={getUrl({ id: root, eleccion, nomenclator, page })}
            onClick={() => setIsOpen(false)}
          >
            {ambitos[root].n}
          </Link>
        </Title>
        <Divider />
        <Title>{`${levelText[constantes.level.departamento]}`}</Title>
        <List
          childs={childs}
          eleccion={eleccion}
          nomenclator={nomenclator}
          onSelection={() => setIsOpen(false)}
          selectedId={fullGenealogy[1] || null}
          page={page}
          parentLevel={parentLevel}
        />
      </LeftPane>
      <RightPane>
        <CloseRow>
          <CloseButton
            onClick={() => setIsOpen(false)}
            aria-label={t('cerrar')}
          >
            <CloseIcon
              width={25}
              height={25}
              fill="#283f5f"
              css={`
                vertical-align: top;
              `}
            />
          </CloseButton>
        </CloseRow>
        <Row>
          {dropdownIds.length === 0 && (
            <TextoInformativo>{t('seleccioneUnDepartamento')}</TextoInformativo>
          )}
        </Row>
        <StyledDropdownGroup
          genealogy={dropdownGenealogy}
          dropdownIds={dropdownIds}
          ambitos={ambitos}
          levelText={levelText}
          nomenclator={nomenclator}
          history={history}
          eleccion={eleccion}
          page={page}
          onSelection={({
            id,
            eleccion,
            nomenclator,
            page,
            parentLevel,
            mesa,
          }) => {
            setIsOpen(false);
            history.push(
              getUrl({ id, eleccion, nomenclator, page, parentLevel, mesa })
            );
          }}
          selectedLevel={mesa ? constantes.level.mesa : ambito.l}
          selectedMesa={mesa ? mesa : undefined}
          variant="desktop"
          parentLevel={parentLevel}
        />
      </RightPane>
    </div>
  );
});

export const NavMenuMobile = withRouter(
  ({
    ambito,
    eleccion,
    nomenclator,
    page,
    history,
    setIsOpen,
    className
  }) => {
    const t = useTranslate();
    const levelText = {
      [constantes.level.departamento]: t('departamentosDC'),
      [constantes.level.municipio]: t('municipios'),
      [constantes.level.zona]: t('zonas'),
      [constantes.level.comuna]: t('comunas'),
      [constantes.level.puesto]: t('puestos'),
      [constantes.level.mesa]: t('mesas'),
    };
    const mesa = useMesa();
    const initialParentLevel = useParentLevel();
    const [selectedId, setSelectedIdTmp] = useState(ambito.i);
    const [idWithMesa, setIdWithMesa] = useState(mesa || ambito.i);
    const [parentLevel, setParentLevel] = useState(initialParentLevel);


    const isMesaSelected = idWithMesa.toString()[0] === 'm';
    const setSelectedId = React.useCallback(
      ({ id }) => {
        const idStr = id.toString();
        if (idStr[0] === 'm') {
          setSelectedIdTmp(Number(id.toString().slice(7)));
          setIdWithMesa(id);
        } else {
          setSelectedIdTmp(id);
          setIdWithMesa(id);
          if (
            nomenclator.ambitos[eleccion][id].l === constantes.level.zona ||
            nomenclator.ambitos[eleccion][id].l === constantes.level.comuna
          ) {
            setParentLevel(nomenclator.ambitos[eleccion][id].l);
          }
        }
      },
      [nomenclator.ambitos, eleccion]
    );

    const ambitos = nomenclator.ambitos[eleccion];
    const genealogy = [
      ...getParents(selectedId, eleccion, nomenclator, parentLevel).reverse(),
      selectedId,
    ];
    const root = genealogy[0];
    let dropdownIds = genealogy;
    if (
      ambitos[selectedId].l !== constantes.level.puesto &&
      (!ambitos[selectedId].h || ambitos[selectedId].h.length === 0)
    ) {
      dropdownIds = dropdownIds.slice(0, -1);
    }
    return (
      <Container className={className}>
        <Row>
          <Title>
            <button
              css={`
                border: none;
                background: transparent;
                &.focus-visible {
                  text-decoration: underline !important;
                  outline: none !important;
                }
              `}
              onClick={() => setSelectedId(root)}
            >
              {ambitos[root].n}
            </button>
          </Title>
        </Row>
        <Divider />

        <DropdownGroup
          genealogy={genealogy}
          dropdownIds={dropdownIds}
          ambitos={ambitos}
          levelText={levelText}
          nomenclator={nomenclator}
          eleccion={eleccion}
          page={page}
          onSelection={setSelectedId}
          selectedLevel={
            isMesaSelected ? constantes.level.mesa : ambitos[selectedId].l
          }
          selectedMesa={isMesaSelected ? idWithMesa.toString() : undefined}
          variant="mobile"
          parentLevel={parentLevel}
        />
        <ConfirmButtonWrapper>
          <ConfirmButton
            onClick={() => {
              history.push(
                getUrl({
                  id: idWithMesa.toString()[0] == 'm' ? idWithMesa : selectedId,
                  eleccion,
                  nomenclator,
                  page,
                  parentLevel
                })
              );
              setIsOpen(false);
            }}
          >
            {t('aceptar')}
          </ConfirmButton>
        </ConfirmButtonWrapper>
      </Container>
    );
  }
);

const Container = styled.div`
  display: flex;
  flex-flow: column;
  padding: 10px;
  align-content: flex-start;
  flex-grow: 1;
`;
const ConfirmButtonWrapper = styled.div`
  margin-top: auto;
`;
const ConfirmButton = styled.button`
  border: none;
  background: #1C4183;
  color: white;
  font-weight: bold;
  text-align: center;
  vertical-align: center;
  width: 100%;
  height: 50px;
  text-transform: uppercase;
  font-size: 14px;
`;
const DropdownGroup = ({
  genealogy,
  dropdownIds,
  ambitos,
  levelText,
  nomenclator,
  eleccion,
  page,
  selectedLevel,
  selectedMesa,
  onSelection,
  variant,
  className,
  parentLevel,
}) => {
  return dropdownIds.map((id, index) => {
    const areChildrenMesas = ambitos[id].l === constantes.level.puesto;
    console.log(areChildrenMesas);
    const childrn = areChildrenMesas
      ? Array(ambitos[id].m || 0)
          .fill()
          .map((_, index) => ({
            i: `m${(index + 1).toString().padStart(6, '0')}${id}`, //le ponemos el index del puesto
            n: `MESA ${index + 1}`,
            c: '',
            s: '',
            m: 0,
            l: constantes.level.mesa,
            h: [],
            p: [id],
          }))
      : ambitos[id].h.map(id => ambitos[id]);

    const levels = {};
    for (const child of childrn) {
      if (levels[child.l]) {
        levels[child.l].push(child);
      } else {
        levels[child.l] = [child];
      }
    }
    console.log("selected levelll"+selectedLevel);
    const hasSelectedItem =
      selectedLevel === constantes.level.mesa
        ? true
        : index !== genealogy.length - 1 && selectedLevel >= ambitos[id].l;
    const selectedId =
      areChildrenMesas &&
      selectedMesa &&
      selectedLevel === constantes.level.mesa
        ? selectedMesa
        : hasSelectedItem && ambitos[genealogy[index + 1]].i;
    const isLast = index === dropdownIds.length - 1;
    const entries = Object.entries(levels);
    return entries.map((k, index) => {
      const [level, children] = k;
      let actuallyHasSelectedItem = hasSelectedItem;
      console.log(parentLevel, level, levelText[level]);
      if (
        (parentLevel === Number(constantes.level.comuna) &&
          Number(level) === Number(constantes.level.zona)) ||
        (parentLevel === Number(constantes.level.zona) &&
          Number(level) === Number(constantes.level.comuna))
      ) {
        actuallyHasSelectedItem = false;
      }
      return (
        children.length > 0 && (
          <DropdownGroupRow className={className} key={`${id}${level}`}>
            <Row>
              <StyledDropdown
                items={children}
                defaultButtonText={`${levelText[level]} (${children.length})`}
                nomenclator={nomenclator}
                eleccion={eleccion}
                defaultSelectedId={selectedId}
                hasSelectedItem={actuallyHasSelectedItem}
                openOnMount={
                  isLast &&
                  index === entries.length - 1 &&
                  variant === 'desktop'
                }
                onSelection={onSelection}
                page={page}
                variant={variant}
                parentLevel={parentLevel}
                selectedMesa={selectedMesa}
              />
            </Row>
            {!(isLast && index === entries.length - 1) && (
              <Row>
                <Divider width="100%" />
              </Row>
            )}
          </DropdownGroupRow>
        )
      );
    });
  });
};

const StyledNavMenu = styled(NavMenu)`
  position: relative;
`;
export default StyledNavMenu;

const Row = styled.div`
  display: flex;
  width: 100%;
`;

const DropdownGroupRow = styled.div`
  display: flex;
  width: 100%;
  flex-flow: row wrap;
  align-items: flex-start;
  align-content: flex-start;
`;

const StyledDropdownGroup = styled(DropdownGroup)`
  ${Row} {
    padding-right: 60px;
  }
`;

const OpenButton = styled.button`
  width: 100%;
  height: 100%;
  border: none;
  background: transparent;
  padding: 14px;
  border-radius: 4px;
  vertical-align: middle;
  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
`;
const StyledInlineDialog = styled(InlineDialog)`
  position: absolute;
  width: 500px;
  height:auto;
  box-shadow: 0 -5px 30px 0 rgba(0, 0, 0, 0.2);
  margin: 0;
  background: white;
  padding: 0px;
  z-index: 1000;
  margin-top: 15px;
  top: 44px;
`;

const StyledNavMenuContent = styled(NavMenuContent)`
  display: flex;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  position: relative;
  :before {
    position: absolute;
    content: '';
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-bottom: 10px solid #f3f3f1;
    top: -10px;
    left: 10px;
  }
`;

const CloseButton = styled.button`
  align-self: flex-end;
  background: transparent;
  border: none;
  margin-left: auto;
  padding: 10px 10px 5px 10px;
  margin-bottom: 0px;
`;
const CloseRow = styled.div`
  display: flex;
  width: 100%;
`;

const LeftPane = styled.div`
  flex: 0 0 220px;
  background-color: #f3f3f1;
  padding: 40px 20px 20px 20px;
  margin: 0 00px 0 0;
  box-sizing: border-box;
`;

const RightPane = styled.div`
  flex-grow: 1;
  display: flex;
  flex-flow: row wrap;
  align-items: flex-start;
  align-content: flex-start;
  width: auto;
  padding-left: 30px;
`;

const Title = styled.div`
  margin: 20px 0;
  font-weight: bold;
  font-size: 14px;
  text-transform: uppercase;
  font-weight: 700;
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  margin: 0;
  flex-shrink: 0;
  background-color: rgba(0, 0, 0, 0.12);
`;

const TextoInformativo = styled.p`
  font-size: 14px;
  font-style: italic;
  line-height: 1.25;
  padding-top: 20px;
`;
const StyledDropdown = styled(Dropdown)`
  width: 100%;
`;
